import { Link, useLocation } from "react-router-dom";
import {
  Box,
  useMediaQuery,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer as MuiDrawer,
  useTheme,
  Chip,
} from "@mui/material";
import { Drawer } from "./index.styles";

import { default as DashboardIcon } from "../../assets/dashboard.svg";
import { default as SearchIcon } from "../../assets/sidebar/search.svg";
import { default as HistoryIcon } from "../../assets/sidebar/history.svg";
import { default as ChromeIcon } from "../../assets/sidebar/chrome.svg";
import { default as AccountIcon } from "../../assets/sidebar/account.svg";
import { default as LogOutIcon } from "../../assets/sidebar/logout.svg";
import { default as SettingIcon } from "../../assets/sidebar/setting.svg";
import { default as QRIcon } from "../../assets/sidebar/scan.svg";
import { default as UploadIcon } from "../../assets/sidebar/upload.svg";
import HeaderProductSearch from "../../components/HeaderProductSearch";
import { useLogout } from "../../hooks/useLogout";

interface SidebarItem {
  label?: string;
  to?: string;
  icon?: string;
  divider?: boolean;
  section?: string;
  size?: number;
  comingSoon?: boolean;
}

const sidebarConfig: SidebarItem[] = [
  {
    to: "#",
    icon: DashboardIcon,
    label: "Dashboard",
    size: 20,
    comingSoon: true,
  },
  { divider: true },
  { section: "Product Analyzer" },
  {
    to: "/product-analyzer",
    icon: SearchIcon,
    label: "Product Analyzer",
    size: 18,
  },
  { to: "/history", icon: HistoryIcon, label: "History", size: 18 },
  { divider: true },
  { section: "Bulk Analyzer" },
  {
    to: "/bulk-analyzer",
    icon: QRIcon,
    label: "Bulk Analyzer",
    size: 18,
    comingSoon: true,
  },
  {
    to: "/uploads",
    icon: UploadIcon,
    label: "Upload File",
    size: 20,
    comingSoon: true,
  },
  { divider: true },
  // { section: "Seller" },
  // { to: "/sellers", icon: SellerIcon, label: "Sellers", size: 18 },
  // { to: "/sellers", icon: MapIcon, label: "Seller Map", size: 18 },
  // { to: "/sellers", icon: BoxIcon, label: "Supplier", size: 18 },
  // { divider: true },
  { section: "Download App" },
  // { to: "/download", icon: PhoneIcon, label: "Mobile App", size: 18 },
  {
    to: "https://chromewebstore.google.com/detail/sellerscout/jalhmaecfpoocghfaocbaehfhobagnhh",
    icon: ChromeIcon,
    label: "Chrome Extension",
    size: 18,
  },
  { divider: true },
  { section: "My Account" },
  { to: "/account", icon: AccountIcon, label: "Account", size: 20 },
  { to: "/settings", icon: SettingIcon, label: "Settings", size: 22 },
];

const SidebarListItem: React.FC<{
  item: SidebarItem;
  isOpen: boolean;
  onClick?: () => void;
}> = ({ item, isOpen, onClick }) => {
  const location = useLocation();
  const isActive = item.to && location.pathname.startsWith(item.to);

  if (item.divider) {
    return isOpen ? (
      <Divider
        sx={{
          margin: 0,
          borderColor: "#E3E3E3B2",
          borderWidth: "1px",
          marginTop: "10px",
        }}
      />
    ) : null;
  }

  if (item.section) {
    return isOpen ? (
      <ListItem
        sx={{
          fontWeight: 500,
          fontSize: 10,
          lineHeight: "100%",
          userSelect: "none",
          color: "#333333B2",
          paddingLeft: "20px",
        }}
      >
        {item.section}
      </ListItem>
    ) : null;
  }

  if (!item.to || !item.icon) return null;

  if (item.comingSoon) {
    return (
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "30px",
          background: "transparent",
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            width: isOpen ? "18px" : "36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: isOpen ? "18px" : "36px",
            borderRadius: "4px",
            mr: isOpen ? "12px" : 0,
            ml: isOpen ? "20px" : 0,
            backgroundColor: "transparent",
          }}
        >
          <img src={item.icon} alt={item.label} />
        </ListItemIcon>
        {isOpen && (
          <ListItemText
            primary={
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#333333",
                }}
              >
                {item.label}
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{ fontSize: "8px", height: "14px", marginLeft: "4px" }}
                />
              </Box>
            }
          />
        )}
      </ListItem>
    );
  }

  return (
    <ListItem
      disablePadding
      component={Link}
      to={item.to}
      onClick={onClick}
      target={
        item.to.startsWith("/") || item.to.startsWith("#") ? "_self" : "_blank"
      }
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "30px",
        background: isOpen && isActive ? "#8F9ED433" : "transparent",
        "&:hover": {
          backgroundColor: "#8F9ED433",
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          width: isOpen ? "18px" : "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: isOpen ? "18px" : "36px",
          borderRadius: "4px",
          mr: isOpen ? "12px" : 0,
          ml: isOpen ? "20px" : 0,
          backgroundColor: !isOpen && isActive ? "#8F9ED433" : "transparent",
        }}
      >
        <img src={item.icon} alt={item.label} />
      </ListItemIcon>
      {isOpen && (
        <ListItemText
          primary={
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#333333",
              }}
            >
              {item.label}
            </Box>
          }
        />
      )}
    </ListItem>
  );
};

const MOBILE_DRAWER_WIDTH = 280;

type TSidebarProps = {
  isOpen: boolean;
  setIsMenuOpen: any;
  window?: () => Window;
  handleDrawerTransitionEnd: any;
  handleDrawerClose: any;
};

export const Sidebar = ({
  isOpen,
  setIsMenuOpen,
  window,
  handleDrawerClose,
  handleDrawerTransitionEnd,
}: TSidebarProps) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));

  const { logout } = useLogout();

  const drawer = (
    <List
      sx={{
        pt: isMobileView ? 3 : 3,
        display: "flex",
        flexDirection: "column",
        gap: isOpen ? 0 : "8px",
      }}
    >
      {isMobileView && (
        <>
          <ListItem
            sx={{ display: "flex", justifyContent: "start", pt: 1, pb: 2 }}
          >
            <HeaderProductSearch onSearchEnd={handleDrawerClose} />
          </ListItem>
        </>
      )}
      {sidebarConfig.map((item, idx) => (
        <SidebarListItem
          key={idx}
          item={item}
          isOpen={isOpen}
          onClick={isMobileView ? handleDrawerClose : undefined}
        />
      ))}
      <SidebarListItem
        item={{ to: "#", label: "Sign Out", icon: LogOutIcon, size: 22 }}
        isOpen={isOpen}
        onClick={logout}
      />
    </List>
  );

  // Remove this const when copying and pasting into your project.
  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box
      component="nav"
      sx={{ width: { sm: MOBILE_DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      aria-label="mailbox folders"
    >
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <MuiDrawer
        container={container}
        variant="temporary"
        open={isOpen}
        onTransitionEnd={handleDrawerTransitionEnd}
        onClose={handleDrawerClose}
        sx={{
          display: { sm: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: MOBILE_DRAWER_WIDTH,
          },
        }}
        slotProps={{
          root: {
            keepMounted: true, // Better open performance on mobile.
          },
        }}
      >
        {drawer}
      </MuiDrawer>
      <Drawer
        variant="permanent"
        onMouseEnter={() => setIsMenuOpen(true)}
        onMouseLeave={() => setIsMenuOpen(false)}
        open={isOpen}
        sx={{
          transition: "0.2s all linear",
          display: { xs: "none", sm: "none", md: "block" },
          "& .MuiDrawer-paper": {
            marginTop: "70px",
            width: isOpen ? "230px" : "inherit",
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
